import { useState } from 'react'
import {
  CheckCircleIcon,
  PaperAirplaneIcon,
  EnvelopeIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/solid'
import { motion } from 'framer-motion'
import { submitContactForm } from '@/lib/queries/admin'

const CATEGORIES = [
  { value: 'general', label: 'General Question' },
  { value: 'account', label: 'Account / Billing' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
]

export default function Support() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState('')

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (!form.email.trim()) {
      setError('Please enter your email so we can get back to you.')
      return
    }
    if (!form.subject.trim()) {
      setError('Please enter a subject.')
      return
    }
    if (!form.message.trim()) {
      setError('Please describe how we can help.')
      return
    }

    setSubmitting(true)
    try {
      await submitContactForm('support', {
        name: form.name.trim() || null,
        email: form.email.trim(),
        subject: form.subject.trim(),
        category: form.category,
        message: form.message.trim(),
      })
      setSucceeded(true)
    } catch {
      setError('Something went wrong. Please try again or email us directly.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--outline-ghost)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all'

  if (succeeded) {
    return (
      <section className="py-20 md:py-28 px-4 md:px-8">
        <div className="max-w-xl mx-auto glass-panel p-8 md:p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--gold)]/20 flex items-center justify-center">
            <CheckCircleIcon className="w-8 h-8 text-[var(--gold)]" aria-hidden="true" />
          </div>
          <h3 className="font-heading text-[var(--text-primary)] text-2xl md:text-3xl font-bold mb-3">
            Message Received!
          </h3>
          <p className="text-[var(--text-muted)] leading-relaxed">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="py-20 md:py-28 px-4 md:px-8">
      <motion.div
        className="max-w-xl mx-auto"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="glass-panel p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="section-badge mx-auto mb-4">
              <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" aria-hidden="true" />
              We're Here to Help
            </div>
            <h1 className="font-heading text-[var(--gold)] text-2xl md:text-3xl font-bold mb-3">
              Customer Support
            </h1>
            <p className="text-[var(--text-muted)] leading-relaxed">
              Have a question or need help? Fill out the form below and we'll get back to you
              shortly.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name & Email row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                aria-label="Your name (optional)"
                placeholder="Your Name (optional)"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                className={inputClass}
              />
              <input
                type="email"
                aria-label="Your email"
                placeholder="Your Email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                required
                className={inputClass}
              />
            </div>

            {/* Subject */}
            <input
              type="text"
              aria-label="Subject"
              placeholder="Subject"
              value={form.subject}
              onChange={e => update('subject', e.target.value)}
              required
              className={inputClass}
            />

            {/* Category */}
            <select
              value={form.category}
              onChange={e => update('category', e.target.value)}
              aria-label="Request category"
              className={inputClass + ' cursor-pointer'}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Message */}
            <textarea
              aria-label="Your message"
              placeholder="How can we help?"
              value={form.message}
              onChange={e => update('message', e.target.value)}
              required
              rows={5}
              className={inputClass + ' resize-none'}
            />

            {/* Error */}
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--gold)] text-[var(--text-on-gold)] font-bold py-3.5 rounded-xl text-lg transition-shadow duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              whileHover={{ y: -2, boxShadow: '0 8px 30px var(--glow-color)' }}
              whileTap={{ scale: 0.97 }}
            >
              {submitting ? (
                'Sending...'
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5" aria-hidden="true" />
                  Send Message
                </>
              )}
            </motion.button>
          </form>

          {/* Direct email fallback */}
          <div className="mt-8 pt-6 border-t border-[var(--outline-ghost)] text-center">
            <p className="text-[var(--text-muted)] text-sm mb-2">
              You can also reach us directly at:
            </p>
            <a
              href="mailto:support@illuminatefaithai.com"
              className="inline-flex items-center gap-2 text-[var(--gold)] hover:underline font-medium"
            >
              <EnvelopeIcon className="w-4 h-4" aria-hidden="true" />
              support@illuminatefaithai.com
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  )
}
